import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../assets/swise_black.png';
import emailIcon from '../assets/email.png';
import CountdownTimer from '../components/CountdownTimer';
import LeftSide from '../components/LeftSide'
import { useTranslation } from 'react-i18next';
import { validateOTP } from '../api/otpAPI'
import { sessionLogin } from '../api/userAPI';

const OTP = () => {
  const navigate = useNavigate();
  const otpCardRef = useRef<HTMLDivElement>(null);
  const [otpHeight, setLoginHeight] = useState<number | undefined>(undefined);
  const location = useLocation();
  const email = (location.state as { email: string })?.email || '';
  const [serverError, setServerError] = useState<string | null>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    const storedLang = localStorage.getItem('language') || 'EN';
    i18n.changeLanguage(storedLang);
  }, []);

  useEffect(() => {
  const token = sessionStorage.getItem('authToken');
    if (token) {
      navigate('/viewer', { replace: true });
    }
    if (!email) {
      navigate('/login', { replace: true });
    }
  }, []);


  useEffect(() => {
    if (!otpCardRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        setLoginHeight(entry.contentRect.height);
      }
    });

    resizeObserver.observe(otpCardRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  // ---------------- OTP Logic ----------------
  const [otp, setOtp] = useState(Array(6).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const handleOtpChange = (index: number, value: string) => {
    if (!/^\d?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleSubmit = async () => {
    setServerError(null);  // clean old errors
    const code = otp.join('');

    if (code.length !== 6) {
      setServerError('Please enter the full 6-digit OTP code.');
      return;
    }

    try {
      const { token } = await validateOTP(email, code, 'vdr');
      sessionStorage.setItem('authToken', token); 
      console.log('[handleSubmit] OTP validated successfully. Token:', token);
      await sessionLogin();
      navigate('/viewer', { state: { email } });
    } catch (err: any) {
      console.error('[handleSubmit] Error validating OTP:', err.message);
      setServerError(err.message || 'Invalid OTP code.');
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('Text').trim();

    if (/^\d{6}$/.test(pastedData)) {
      const newOtp = pastedData.split('');
      setOtp(newOtp);

      inputRefs.current[5]?.focus();
    }
  };


  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      {/* Left Side */}
      <Box
        sx={{
          width: '60%',
          bgcolor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <LeftSide minHeight={otpHeight} />
      </Box>

      {/* Right Side */}
      <Box
        sx={{
          width: '40%',
          bgcolor: 'black',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box
          ref={otpCardRef}
          sx={{
            width: '80%',
            bgcolor: 'white',
            borderRadius: 2,
            p: 4,
            boxShadow: 3,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <img
            src={logo}
            alt="Swise Logo"
            style={{
              width: '100px',
              height: 'auto',
              marginBottom: '56px',
              alignSelf: 'flex-start',
            }}
          />
          <img
              src={emailIcon}
              alt="Email"
              style={{
                width: '60px',
                height: '60px',
                marginBottom: '30px',
              }}
          />
          <Typography
            gutterBottom
            sx={{
              alignSelf: 'center',
              fontWeight: 'bold',
              fontSize: '18px',
              color: 'black',
              mb: 4
            }}
          >
            {t('sentOTPEmail')} {email} 
          </Typography>
          <Typography
            sx={{
              alignSelf: 'center',
              fontSize: '14px',
              color: 'black',
              mb: 4
            }}
          >
            {t('checkJunkFolder')}
          </Typography>
          <Typography
            gutterBottom
            sx={{
              alignSelf: 'centert',
              fontWeight: 'normal',
              fontSize: '17px',
              color: 'grey',
            }}
          >
            {t('enterOTP')}
          </Typography>

          {/* OTP input fields */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center', 
              gap: 1.5,                  
              mt: 3,
              mb: 3,
            }}
          >
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={(el) => (inputRefs.current[index] = el)}
                value={digit}
                onChange={(e) => handleOtpChange(index, e.target.value)}
                onKeyDown={(e) => handleKeyDown(index, e)}
                inputProps={{
                  onPaste: handlePaste,
                  maxLength: 1,
                  style: {
                    textAlign: 'center',
                    fontSize: '24px',
                    padding: '12px 0',
                  },
                }}
                sx={{
                  width: '50px',
                  '& .MuiInputBase-root': {
                    borderRadius: '8px',
                  },
                }}
              />
            ))}
          </Box>
          {serverError && (
            <Typography sx={{ color: 'red', mt: 1 }}>
              {serverError}
            </Typography>
          )}
          <CountdownTimer duration={60} storageKey="otp-timer" onExpire={() => console.log('OTP expired')} />
          <Button
            variant="contained"
            fullWidth
            onClick={handleSubmit}
            sx={{
              mt: 2,
              height: '45px', // 56px == standard
              backgroundColor: '#73AF73',
              color: '#fff',
              '&:hover': {
                backgroundColor: '#A8CFA9',
              },
            }}
          >
            {t('validate')}
          </Button>

          <Typography
            sx={{
              fontSize: '16px',
              color: 'grey',
              mt: 3,
              mb: 0,
              alignSelf: 'flex-end',
            }}
          >
            {t('havingProblems')}
          </Typography>
          <Typography
            component="a"
            href="mailto:it@swise.com"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              fontSize: '16px',
              color: '#A8CFA9',
              mt: 0,
              mb: 5,
              alignSelf: 'flex-end',
              textDecoration: 'none',
              '&:hover': {
                textDecoration: 'underline',
                color: '#1976d2',
              },
            }}
          >
            {t('contactSupport')}
          </Typography>
          <Typography
            sx={{
              fontSize: '15px',
              color: 'grey',
              mt: 3,
              alignSelf: 'flex-start',
            }}
          >
            © 2025 Swise AG
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OTP;